import React from 'react';
import SEO from '../components/SEO';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

const Privacy = () => {
  return (
    <>
      <SEO 
        title="Privacy Policy | Symbionic"
        description="Privacy Policy for Symbionic and VerifAEye — How we collect, use, and protect your personal information."
        canonical="https://symbionic.com.au/privacy"
      />
      <div className="min-h-screen bg-slate-50 py-12">
        <div className="max-w-4xl mx-auto px-6">
          {/* Back button */}
          <Link 
            to="/" 
            className="inline-flex items-center text-cyan-600 hover:text-cyan-700 mb-8 group"
          >
            <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" />
            Back to Home
          </Link>

          {/* Content */}
          <div className="bg-white rounded-2xl shadow-lg p-8 lg:p-12">
            <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
              Privacy Policy
            </h1>
            <p className="text-slate-600 mb-12">
              <strong>Effective date:</strong> 27 December 2025
            </p>

            <div className="prose prose-slate max-w-none space-y-8">
              <p className="text-lg text-slate-700 leading-relaxed">
                This Privacy Policy describes how Bruce Lawrence Visser trading as Symbionic (ABN 88 549 501 774) ("we", "us", "our") collects, uses, and protects your personal information when you use VerifAEye and our related services (the "Service").
              </p>

              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">1. Information We Collect</h2>
                <p className="text-slate-700 leading-relaxed mb-3">
                  We collect the following types of information:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-slate-700">
                  <li><strong>Account Information:</strong> Name, email address, and authentication details when you create an account.</li>
                  <li><strong>Images and Content:</strong> Photos and documents you upload to the Service for analysis and documentation.</li>
                  <li><strong>Usage Data:</strong> Information about how you use the Service, including features accessed and actions taken.</li>
                  <li><strong>Device Information:</strong> Browser type, device type, IP address, and operating system.</li>
                  <li><strong>Google Account Information:</strong> When you connect Google Drive, we access your Google account information and files as authorized by you.</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">2. How We Use Your Information</h2>
                <p className="text-slate-700 leading-relaxed mb-3">
                  We use your information to:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-slate-700">
                  <li>Provide and maintain the Service, including AI analysis of your images</li>
                  <li>Store your images and data in your connected Google Drive account</li>
                  <li>Process payments through our payment processor (Stripe)</li>
                  <li>Communicate with you about your account and the Service</li>
                  <li>Improve and develop new features for the Service</li>
                  <li>Detect and prevent fraud or misuse of the Service</li>
                  <li>Comply with legal obligations</li>
                </ul>
              </section>

              <section className="bg-cyan-50 border-l-4 border-cyan-500 p-6 rounded-r-lg">
                <h2 className="text-2xl font-bold text-slate-900 mb-4">3. Google API Services User Data Policy</h2>
                <p className="text-slate-700 leading-relaxed mb-3">
                  <strong>VerifAEye's use of information received from Google APIs adheres to the{' '}
                  <a 
                    href="https://developers.google.com/terms/api-services-user-data-policy" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-cyan-600 hover:text-cyan-700 font-semibold underline"
                  >
                    Google API Services User Data Policy
                  </a>, including the Limited Use requirements.</strong>
                </p>
                <p className="text-slate-700 leading-relaxed mb-3">
                  Specifically:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-slate-700">
                  <li>We only access your Google Drive files that you explicitly authorize us to access</li>
                  <li>We use this access solely to store and retrieve your uploaded images and generated reports in your own Google Drive</li>
                  <li>We do not transfer your Google user data to third parties (except as necessary to provide the Service, e.g., for AI analysis)</li>
                  <li>We do not use your Google user data for serving advertisements</li>
                  <li>We do not allow humans to read your Google user data unless it is necessary for security purposes, to comply with applicable law, or you have given us explicit consent</li>
                </ul>
                <p className="text-slate-700 leading-relaxed mt-3">
                  You can revoke our access to your Google account at any time through your Google Account settings.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">4. Data Storage and Security</h2>
                <p className="text-slate-700 leading-relaxed mb-3">
                  Your images are stored in your own Google Drive account. We do not maintain permanent copies of your images on our servers beyond transient processing required for AI analysis.
                </p>
                <p className="text-slate-700 leading-relaxed">
                  We implement reasonable security measures to protect your information, including encryption in transit and access controls. However, no method of transmission or storage is 100% secure.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">5. Third-Party Services</h2>
                <p className="text-slate-700 leading-relaxed mb-3">
                  We use the following third-party services:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-slate-700">
                  <li><strong>Google Drive:</strong> For storing your images and reports</li>
                  <li><strong>Stripe:</strong> For processing payments</li>
                  <li><strong>AI Service Providers:</strong> For analyzing your images and generating descriptions</li>
                </ul>
                <p className="text-slate-700 leading-relaxed mt-3">
                  These services have their own privacy policies and we are not responsible for their practices.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">6. Your Rights</h2>
                <p className="text-slate-700 leading-relaxed mb-3">
                  Under the Australian Privacy Principles, you have the right to:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-slate-700">
                  <li>Access the personal information we hold about you</li>
                  <li>Request correction of inaccurate information</li>
                  <li>Request deletion of your account and associated data</li>
                  <li>Withdraw consent for data processing where consent is the legal basis</li>
                  <li>Lodge a complaint with the Office of the Australian Information Commissioner</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">7. Data Retention</h2>
                <p className="text-slate-700 leading-relaxed">
                  We retain your account information for as long as your account is active or as needed to provide the Service. If you close your account, we will delete or anonymize your information within a reasonable timeframe, except where retention is required by law.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">8. Children's Privacy</h2>
                <p className="text-slate-700 leading-relaxed">
                  The Service is not intended for users under 18 years of age. We do not knowingly collect personal information from children.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">9. Changes to This Policy</h2>
                <p className="text-slate-700 leading-relaxed">
                  We may update this Privacy Policy from time to time. We will notify you of material changes via email or through the Service. Continued use after changes constitutes acceptance.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">10. Contact Us</h2>
                <p className="text-slate-700 leading-relaxed">
                  For questions about this Privacy Policy or to exercise your rights, contact us at:{' '}
                  <a href="mailto:symbionic@gmail.com" className="text-cyan-600 hover:text-cyan-700 font-semibold">
                    symbionic@gmail.com
                  </a>
                </p>
                <p className="text-slate-700 leading-relaxed mt-3">
                  Bruce Lawrence Visser trading as Symbionic<br />
                  ABN 88 549 501 774
                </p>
              </section>
            </div>

            {/* Back to top */}
            <div className="mt-12 pt-8 border-t border-slate-200 text-center">
              <Link 
                to="/" 
                className="inline-flex items-center text-cyan-600 hover:text-cyan-700 font-semibold"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Return to Home
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Privacy;
